<script>
    import { redirect, page, url, isActive } from "@roxi/routify";
    import { v4 as uuid } from "uuid";
    import { slide } from "svelte/transition";
    import SidebarList from "./SidebarList.svelte";
    import { createEventDispatcher, onDestroy, onMount } from "svelte";
    import { TokenStore } from "$/stores/TokenStore";
    import { TenantStore } from "$/stores/TenantStore";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { clickOutside } from "$/utils/clickOutside";
    import {
        ClusterAccessLevel,
        ClusterStore,
        ClusterType,
    } from "$/stores/ClusterStore";
    import { GiftStore } from "$/stores/GiftStore";
    import {
        findElementSiblingsTag,
        getEffectivePricingPlan,
        getRootDocument,
    } from "$/utils/Utils";
    import { AppStore, Panels } from "$/stores/AppStore";
    import { RecentTopicsStore } from "$/stores/RecentTopicsStore";
    import { clickAction } from "$/utils/clickAction";
    import { tooltip } from "$/utils/tooltip";
    import { removeSuggestionList } from "./editor/utils";
    import Sortable from "sortablejs";
    import SVGIcon from "./SVGIcon.svelte";
    import { highlightedTopic, SidebarStore } from "$/stores/SidebarStore";

    export let show = false;

    let showWorkspaceSelectMenu = false;
    let showWorkspaceSelectMenuButton;
    let active;
    let tenants;
    let sortableClustersWrapper;
    let isSnippetOwner = false;
    let isDraggingOverInbox = false;
    let showClustersGroup = true;
    let showSharedGroup = true;
    let toAddWorkspace = false;
    let newWorkspaceName = "";
    let storesLoaded = false;

    // this code handles the animation of list items by persisting them
    // while tenant is being loaded. Without this logic every time tenant
    // is changed list items disappear and reappear.
    let showSettingsAnimation = true;
    let showCopilotAnimation = false;

    const dispatch = createEventDispatcher();
    const CLUSTER_ID_PREFIX = "js-cluster-";
    const dragAndDropConfig = {
        handle: ".sidebar-list-title",
        animation: 150,
        swapThreshold: 0.1,
        delay: 200,
        delayOnTouchOnly: true,
        onStart(event) {
            // Add quick vibration feedback when drag starts
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }

            // Add a clone in the original location
            const persistentClone = event.from.insertBefore(
                event.item.cloneNode(true),
                event.from.children[event.oldIndex + 1],
            );

            // give it a class so we can remove it later
            persistentClone.classList.add("sortable-clone");

            // remove irrelevant classes
            persistentClone.classList.remove(
                "sortable-chosen",
                "sortable-ghost",
            );
        },
        onEnd(event) {
            event.from
                .querySelectorAll(".sortable-clone")
                .forEach((child) => child.remove());
            ClusterStore.changeClustersOrder(event.oldIndex, event.newIndex);

            // Sortable is disabling draggable when dragging the element but doesn't revert it after dropping the element
            // so we need to manually enable draggable again
            setTopicsDraggable(event.item);
        },
    };

    async function loadStores() {
        if ($TenantStore === null) await TenantStore.init();
        if ($ClusterStore?.clusters === null)
            await ClusterStore.init($TenantStore.active.id);

        if ($GiftStore === null) GiftStore.init();

        storesLoaded = true;
    }

    $: effectivePricingPlan = $TenantStore?.active
        ? getEffectivePricingPlan($TenantStore?.active)
        : null;
    $: tenantClusters =
        $ClusterStore &&
        ClusterStore.getClustersByType(ClusterType.TENANT).length;
    $: teamWorkspaceHasExpired = effectivePricingPlan === -1;
    $: isAddingClustersDisabled =
        effectivePricingPlan === -1 ||
        (effectivePricingPlan === 0 &&
            $ClusterStore?.clusters &&
            tenantClusters >= 5);
    $: isEditor =
        $ActiveTopic?.roles.find((r) => r.userId === $TokenStore.user?.id)
            ?.role >= TopicAccessLevel.EDITOR;
    $: myTopicsCluster =
        ($ClusterStore &&
        ClusterStore.getClustersByType(ClusterType.TENANT_DEFAULT)[0]) ?? {};
    $: sortableClusters =
        ($ClusterStore && ClusterStore.getSortableClustersSorted()) ?? [];
    $: archiveCluster =
        ($ClusterStore &&
        ClusterStore.getClustersByType(ClusterType.ARCHIVED)[0]) ?? {};
    $: sharedClustersWithTopics =
        $ClusterStore &&
        ClusterStore.getSharedClusters()?.filter(
            (cluster) => cluster.topics.length > 0,
        );
    $: isFreeUser = effectivePricingPlan === 0;

    $: {
        let draggingSnippet = $ActiveTopic?.snippets?.find(
            (s) => s.id === $ActiveTopicStore?.draggingSnippetId,
        );
        isSnippetOwner =
            draggingSnippet?.createdBy?.id === $TokenStore?.user?.id;
    }

    $: {
        if ($TenantStore) {
            showSettingsAnimation = TenantStore.isOwner($TokenStore?.user?.id);
            showCopilotAnimation =
                $TenantStore.activeAuthorities?.find(
                    (a) => a.authority === "ROLE_COPILOT_BETA",
                ) || effectivePricingPlan === 2;
        }
    }

    $: {
        active = $TenantStore !== null ? $TenantStore.active : null;
        tenants = $TenantStore !== null ? $TenantStore.tenants : null;
    }

    $: {
        if (
            $TenantStore &&
            TenantStore.isOwner($TokenStore?.user?.id) &&
            sortableClustersWrapper
        )
            Sortable.create(sortableClustersWrapper, dragAndDropConfig);
    }

    $: if ($highlightedTopic) {
        scrollToTopic($highlightedTopic);
    }

    // NOTE: If topicId is not present then the index.svelte is called and all stores
    //       are initialized there, because they are needed for a proper workspace redirect.
    //       But if topicId, payment etc. is called then stores are initilized in the sidebar
    if ($page.path !== "/index" && $page.path !== "/:topicId") {
        loadStores();
    } else {
        storesLoaded = true;
    }

    async function handleKeyDown(event) {
        if (event.key === "Enter" && newWorkspaceName !== "") {
            await TenantStore.add({
                name: newWorkspaceName,
            });
            toAddWorkspace = false;
            const refreshedToken = await LoginService.refreshTokenLogin();
            TokenStore.updateTokenStore(refreshedToken);
        }
    }

    async function addNewTopic(cluster) {
        let topic = await ClusterStore.createTopic(
            { name: "Untitled" },
            cluster,
        );
        SidebarStore.highlightTopic(topic.id);
        $redirect(`/${topic.id}`);
    }

    async function selectWorkspace(tenant) {
        // optimistic approach
        showWorkspaceSelectMenu = false;

        // return if same tenant
        if (tenant.id === active.id) {
            return;
        }

        removeSuggestionList();

        // set loading state
        active = null;
        ActiveTopicStore.clear();
        ClusterStore.clear();
        $RecentTopicsStore = null;

        // close sidebar
        dispatch("close");

        // load new tenant
        await TenantStore.setActive(tenant);
        await ClusterStore.init($TenantStore.active.id);
        await RecentTopicsStore.load();

        RecentTopicsStore.populateTopicsFromClusters($ClusterStore.clusters);

        // redirect to home if on topic page
        if ($page.path === "/:topicId") $redirect(`/`);
    }

    async function addCluster(event) {
        event.stopImmediatePropagation();
        event.preventDefault();
        showClustersGroup = true;

        if (isAddingClustersDisabled) {
            $redirect("/payment");
            return;
        }

        // add cluster
        const cluster = await ClusterStore.createCluster({
            name: "Untitled cluster",
            id: uuid(),
        });

        // scroll to element
        const element = document.getElementById(CLUSTER_ID_PREFIX + cluster.id);
        element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
        });

        // add highlight animation
        element.classList.add("cluster-highlight");
        setTimeout(() => {
            element.classList.add("remove");

            setTimeout(() => {
                element.classList.remove("cluster-highlight");
                element.classList.remove("remove");
            }, 1000);
        }, 2000);
    }

    function handleDropOnInbox(event) {
        event.preventDefault();

        // Reset dragging states
        isDraggingOverInbox = false;
        $AppStore.snippetIsDragging = false;

        if (ActiveTopicStore.isInbox() || !isEditor || !isSnippetOwner) {
            return;
        }

        // Update the snippet in the ActiveTopicStore
        ActiveTopicStore.moveSnippetToInbox(
            $ActiveTopicStore.draggingSnippetId,
            $ActiveTopic.id,
        );
    }

    function setTopicsDraggable(htmlElement) {
        const clusterId = htmlElement.getAttribute("data-id");
        const cluster = sortableClusters.find((el) => el.id === clusterId);
        let canMoveTopics = true;

        if (cluster.accessLevel === ClusterAccessLevel.RESTRICTED) {
            canMoveTopics =
                cluster.clusterRoles?.find(
                    (el) =>
                        el.user.id == $TokenStore.user.id &&
                        el.role === TopicAccessLevel.OWNER,
                ) !== undefined;
        }

        let canMoveTopicsFromCluster =
            effectivePricingPlan !== -1 &&
            cluster.clusterType === ClusterType.TENANT &&
            canMoveTopics;
        let clustersTopicLinks = findElementSiblingsTag(htmlElement, "A");
        clustersTopicLinks.forEach((el) =>
            el.setAttribute("draggable", canMoveTopicsFromCluster),
        );
    }

    // The ghost dragging element is actually the default dragging element
    // but it has a box shadow that doesn't look very nice so we need to remove it
    function generateDraggingGhostElement(event) {
        getRootDocument().querySelector("#clusterDragGhost")?.remove();
        const draggingCard = event.srcElement
            .querySelector(".sidebar-list-title")
            ?.cloneNode(true);
        if (draggingCard) {
            const draggingCardGhost = getRootDocument().createElement("div");
            draggingCardGhost.id = "clusterDragGhost";
            draggingCardGhost.style.position = "absolute";
            draggingCardGhost.style.left = "-999em";
            draggingCardGhost.classList.add("bg-gray-200");
            draggingCardGhost.classList.add("rounded");
            draggingCardGhost.innerHTML = draggingCard.innerHTML;
            draggingCardGhost.style.width = event.srcElement.offsetWidth + "px";
            getRootDocument().querySelector("body").append(draggingCardGhost);
            event.dataTransfer.setDragImage(draggingCardGhost, 0, 0);
        }
    }

    function destroyDragingCardGhostElement() {
        getRootDocument().querySelector("#cluster-drag-ghost")?.remove();
    }

    function scrollToTopic(topicId) {
        const topicElement = document.getElementById(`topic-${topicId}`);
        if (topicElement) {
            topicElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest",
            });
        }
    }

    onMount(() => {
        getRootDocument().addEventListener(
            "dragstart",
            generateDraggingGhostElement,
        );
        getRootDocument().addEventListener(
            "dragend",
            destroyDragingCardGhostElement,
        );
    });

    onDestroy(() => {
        getRootDocument().removeEventListener(
            "dragstart",
            generateDraggingGhostElement,
        );
        getRootDocument().removeEventListener(
            "dragend",
            destroyDragingCardGhostElement,
        );
    });
</script>

<div class="sidebar" class:open={show}>
    <div class="sticky-top">
        <div class="workspace-wrapper">
            {#if active}
                <div
                    class="workspace"
                    bind:this={showWorkspaceSelectMenuButton}
                    use:clickAction={() =>
                        (showWorkspaceSelectMenu = !showWorkspaceSelectMenu)}
                >
                    <div class="workspace-icon">
                        {active.name[0].toUpperCase()}
                    </div>
                    <div class="workspace-info">
                        <span class="workspace-info__name">{active.name}</span>
                        <span class="workspace-info__email"
                            >{$TokenStore.user.email}</span
                        >
                    </div>
                    <svg
                        class="workspace-select"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                        />
                    </svg>
                </div>
            {:else}
                <div class="workspace">
                    <div class="workspace-icon" title="L is for loading...">
                        L
                    </div>
                    <div class="workspace-info">
                        <span class="workspace-info__name">Loading...</span>
                        <span class="workspace-info__email"
                            ><i>patience young padawan</i></span
                        >
                    </div>
                </div>
            {/if}

            {#if showWorkspaceSelectMenu}
                <div
                    class="workspace-select__menu"
                    use:clickOutside
                    on:click_outside={(event) => {
                        if (
                            showWorkspaceSelectMenuButton !==
                                event.detail.target &&
                            !showWorkspaceSelectMenuButton.contains(
                                event.detail.target,
                            )
                        ) {
                            showWorkspaceSelectMenu = false;
                        }
                    }}
                >
                    {#each tenants as tenant, i (tenant)}
                        <div
                            class="workspace-select__menu-item"
                            class:workspace-select__menu-item--active={tenant.id ===
                                active.id}
                        >
                            <div
                                class="workspace pb-1 pt-1"
                                use:clickAction={() => selectWorkspace(tenant)}
                            >
                                <div
                                    class="workspace-icon"
                                    class:workspace-icon--active={tenant.id ===
                                        active.id}
                                >
                                    {tenant.name[0].toUpperCase()}
                                </div>
                                <div class="workspace-info">
                                    <span class="workspace-info__name"
                                        >{tenant.name}</span
                                    >
                                    <span class="workspace-info__email"
                                        >{$TokenStore.user.email}</span
                                    >
                                </div>
                            </div>
                        </div>
                    {/each}
                    {#if showSettingsAnimation}
                        <div
                            class="workspace-select__menu-item workspace-select__menu-item--settings"
                        >
                            <a
                                class="settings"
                                href={"#"}
                                on:click={() => {
                                    AppStore.setPanel(Panels.WORKSPACE);
                                    showWorkspaceSelectMenu = false;
                                }}
                            >
                                <div class="settings-icon">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
                                        />
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                    </svg>
                                </div>
                                <div class="settings-text">
                                    <p class="settings-text__placeholder">
                                        Settings and Members
                                    </p>
                                </div>
                            </a>
                        </div>
                    {/if}

                    {#if false}
                        <!-- Not enabling users to add new workspaces by themselves for now -->
                        <div
                            class="workspace-select__menu-item"
                            use:clickOutside
                            on:click_outside={() => (toAddWorkspace = false)}
                        >
                            {#if !toAddWorkspace}
                                <div
                                    class="workspace pb-1 pt-1"
                                    use:clickAction={() =>
                                        (toAddWorkspace = true)}
                                >
                                    <div class="workspace-add">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            class="h-5 w-5"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <div class="workspace-info">
                                        <span class="workspace-info__email"
                                            >Add new workspace</span
                                        >
                                    </div>
                                </div>
                            {:else}
                                <div class="workspace pb-1 pt-1">
                                    <div class="workspace-add">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            class="h-5 w-5"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <div class="workspace-info">
                                        <span class="workspace-info__name"
                                            ><input
                                                class="font-normal italic outline-none"
                                                type="text"
                                                placeholder="press enter to confirm..."
                                                bind:value={newWorkspaceName}
                                                on:keydown={handleKeyDown}
                                            /></span
                                        >
                                    </div>
                                </div>
                            {/if}
                        </div>
                    {/if}
                </div>
            {/if}
        </div>

        <a
            class="settings inbox-drop-zone"
            href={$url("/inbox")}
            class:settings--active={$isActive("/inbox")}
            class:settings--dragging-over={isEditor && isDraggingOverInbox}
            on:dragover={(event) => {
                event.preventDefault();
                isDraggingOverInbox = true;
            }}
            on:dragleave={() => {
                isDraggingOverInbox = false;
            }}
            on:drop={handleDropOnInbox}
            use:clickAction={AppStore.closePanels}
        >
            <div class="settings-icon">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 3.75H6.912a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859M12 3v8.25m0 0-3-3m3 3 3-3"
                    />
                </svg>
            </div>
            <div class="settings-text">
                <p class="settings-text__placeholder">Inbox</p>
            </div>
        </a>

        <a
            class="settings"
            class:settings--active={$isActive("/pdf")}
            use:tooltip={teamWorkspaceHasExpired &&
                "Upgrade to continue uploading PDF's"}
            use:clickAction={AppStore.closePanels}
            href={$url(teamWorkspaceHasExpired ? "/payment" : "/pdf")}
        >
            <div class="settings-icon">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
                    />
                </svg>
            </div>
            <div class="settings-text">
                <p class="settings-text__placeholder">Documents</p>
            </div>

            {#if teamWorkspaceHasExpired}
                <span class="settings-tag settings-tag--yellow"> Upgrade </span>
            {/if}
        </a>

        {#if showCopilotAnimation}
            <a
                class="settings"
                class:settings--active={$isActive("/copilot")}
                use:tooltip={teamWorkspaceHasExpired &&
                    "Upgrade to continue using copilot"}
                use:clickAction={AppStore.closePanels}
                transition:slide
                href={$url(teamWorkspaceHasExpired ? "/payment" : "/copilot")}
            >
                <div class="settings-icon">
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke-width="1.5"
                        stroke="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12.1837 3.00035C9.21514 3.02533 7.1962 4.44946 5.71379 7.07656C5.52922 7.40364 5.29053 7.94121 5.29053 7.94121V15.6613C5.29053 15.6613 6.45803 17.9107 7.70918 18.8111C8.29626 19.2336 8.64503 19.4978 9.34177 19.6757C11.0348 20.1081 13.4535 20.1081 15.2675 19.6757C16.0707 19.4843 16.5358 19.3072 17.2024 18.8111C18.3514 17.9559 19.1373 15.6613 19.1373 15.6613V7.94121C19.1373 7.94121 18.9368 7.40439 18.7745 7.07656C17.4165 4.334 15.1925 2.97502 12.1837 3.00035Z"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M22.1609 8.55253C21.4839 7.75246 19.1367 7.38721 19.1367 7.38721V16.2985C19.1367 16.2985 21.3869 15.2958 22.1609 14.3106C22.3436 14.078 22.5641 13.6937 22.5641 13.6937V9.30656C22.5641 9.30656 22.3907 8.82411 22.1609 8.55253Z"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M2.3873 8.55253C3.03724 7.75246 5.29053 7.38721 5.29053 7.38721V16.2985C5.29053 16.2985 3.13037 15.2958 2.3873 14.3106C2.21191 14.078 2.00021 13.6937 2.00021 13.6937V9.30656C2.00021 9.30656 2.16668 8.82411 2.3873 8.55253Z"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M5.29053 9.99426C5.36794 10.622 6.61601 10.9774 7.23754 11.1544C7.25933 11.1606 7.28035 11.1666 7.30052 11.1723C8.86857 11.6208 10.2985 11.6427 12.2884 11.6364C14.2218 11.6303 15.7546 11.5628 17.1273 11.1701C17.1475 11.1644 17.1685 11.1584 17.1903 11.1522C17.8118 10.9752 19.0599 10.6198 19.1373 9.99205"
                        />
                        <path
                            d="M5.29053 14.1071C5.36794 14.7348 6.61601 15.0902 7.23754 15.2672C7.25933 15.2734 7.28035 15.2794 7.30052 15.2851C8.86857 15.7336 10.2985 15.7555 12.2884 15.7492C14.2218 15.7431 15.7546 15.6756 17.1273 15.2829C17.1475 15.2772 17.1685 15.2712 17.1903 15.265C17.8118 15.088 19.0599 14.7326 19.1373 14.1048"
                        />
                    </svg>
                </div>
                <div class="settings-text">
                    <p class="settings-text__placeholder">Copilot</p>
                </div>
                {#if teamWorkspaceHasExpired}
                    <span class="settings-tag settings-tag--yellow">
                        Upgrade
                    </span>
                {:else}
                    <span class="settings-tag--icon">
                        <svg
                            class="h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                            />
                        </svg>
                    </span>
                {/if}
            </a>
        {/if}
    </div>

    <div class="sidebar-content">
        {#if storesLoaded && $ClusterStore.clusters}
            <div
                class="sidebar-cluster-group sidebar-cluster-group--first group"
                on:click={() => (showClustersGroup = !showClustersGroup)}
            >
                <span>Clusters</span>
                <div
                    class="sidebar-list-title__button"
                    use:tooltip={isAddingClustersDisabled
                        ? "Upgrade to create unlimited clusters"
                        : "Add New Cluster"}
                    on:click={addCluster}
                >
                    <SVGIcon name="plus" size="16" height="16" />
                </div>
            </div>

            {#if showClustersGroup}
                <div class="sidebar-list-wrapper">
                    <SidebarList
                        cluster={myTopicsCluster}
                        addNewClicked={() => addNewTopic(myTopicsCluster)}
                    ></SidebarList>
                </div>

                <div
                    bind:this={sortableClustersWrapper}
                    class="sortable-clusters-wrapper"
                >
                    {#each sortableClusters as cluster (cluster.id)}
                        <div
                            id={CLUSTER_ID_PREFIX + cluster.id}
                            data-id={cluster.id}
                            class="sidebar-list-wrapper"
                        >
                            <SidebarList
                                {cluster}
                                addNewClicked={() => addNewTopic(cluster)}
                            ></SidebarList>
                        </div>
                    {/each}
                </div>

                <div class="sidebar-list-wrapper">
                    <SidebarList
                        cluster={archiveCluster}
                        addNewClicked={() => addNewTopic(archiveCluster)}
                    ></SidebarList>
                </div>
            {/if}

            {#if TenantStore.isPersonal() && sharedClustersWithTopics.length > 0}
                <div
                    class="sidebar-cluster-group"
                    on:click={() => (showSharedGroup = !showSharedGroup)}
                >
                    Shared
                </div>
                {#if showSharedGroup}
                    {#each sharedClustersWithTopics as cluster}
                        <div
                            id={CLUSTER_ID_PREFIX + cluster.id}
                            class="sidebar-list-wrapper"
                        >
                            <SidebarList
                                {cluster}
                                addNewClicked={() => addNewTopic(cluster)}
                            ></SidebarList>
                        </div>
                    {/each}
                {/if}
            {/if}
        {:else}
            <div class="settings settings--loading">
                <div class="settings-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                        />
                    </svg>
                </div>
                <div class="settings-text">
                    <p class="settings-text__placeholder">Loading topics...</p>
                </div>
            </div>
        {/if}

        <div class="plan-features-footer">
            {#if isFreeUser}
                <button
                    class="plan-features-button"
                    class:settings--active={$isActive("/payment")}
                    on:click={() => {
                        $redirect("/payment");
                        AppStore.closePanels();
                    }}
                >
                    <div class="flex items-center">
                        <div class="settings-text">
                            <p class="settings-text__placeholder">
                                Upgrade your <b>Plan & Features</b>
                            </p>
                        </div>
                        <div class="plan-features-button-icon">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
                                />
                            </svg>
                        </div>
                    </div>
                </button>
            {/if}
        </div>
    </div>
</div>

<style>
    .sidebar {
        @apply absolute -left-full z-50 flex h-full w-full flex-col bg-gray-100 py-2 pl-2 shadow-none transition-all sm:w-72 sm:min-w-[288px] sm:shadow-2xl md:relative md:-left-0 md:shadow-none;
    }

    .sidebar.open {
        @apply -left-0;
    }

    .sidebar::-webkit-scrollbar-thumb {
        @apply bg-gray-200 hover:bg-gray-300;
    }

    .sticky-top {
        @apply sticky top-0 z-20 bg-gray-100 pr-2;
    }

    .sidebar-content {
        @apply relative flex h-full flex-col overflow-y-auto pr-2;
    }

    .workspace-wrapper {
        @apply z-20 inline;
    }

    .workspace {
        @apply flex cursor-pointer select-none flex-row items-center gap-2 rounded p-2 hover:bg-gray-200;
    }

    .workspace-icon {
        @apply inline-block h-5 w-5 min-w-fit flex-shrink-0 rounded-sm bg-gray-900 bg-opacity-10 text-center text-sm text-gray-500;
    }

    .workspace-add {
        @apply inline-block h-5 w-5 min-w-fit rounded-sm bg-opacity-10 text-center text-sm text-gray-500;
    }

    .workspace-info {
        @apply flex min-w-0 flex-1 flex-col truncate;
    }

    .workspace-info__name {
        @apply truncate text-sm font-bold;
    }

    .workspace-info__email {
        @apply truncate text-xs;
    }

    .workspace-select {
        @apply ml-auto h-5 w-5 text-gray-600;
    }

    .workspace-select__menu {
        @apply absolute left-0 right-0 z-10 ml-auto mr-auto max-h-[calc(100dvh-200px)] w-[94%] overflow-y-auto rounded-md bg-white px-2 pt-2 shadow-lg;
    }

    .workspace-select__menu-item--active {
        @apply rounded bg-yellow-100;
    }

    .workspace-select__menu-item:last-child {
        @apply mb-2;
    }

    .workspace-select__menu-item--settings {
        @apply sticky bottom-0 !mb-0 mt-1 bg-white pb-2 shadow-[0_-8px_6px_-1px_rgba(255,255,255,1)] shadow-white;
    }

    .settings {
        @apply relative flex select-none flex-row items-center gap-2 rounded px-2 py-1 hover:cursor-pointer hover:bg-gray-200;
    }

    .settings--active {
        @apply font-bold;
    }

    .settings--loading {
        @apply pointer-events-none mt-4 cursor-default select-none opacity-30 hover:bg-transparent;
    }

    .settings--loading svg {
        @apply animate-spin;
    }

    .settings.settings--dragging-over {
        @apply bg-gray-200;
    }

    .settings-icon {
        @apply inline-block h-4 w-4 text-center text-sm;
    }

    .settings-text {
        @apply mr-auto flex flex-col;
    }

    .settings-text__placeholder {
        @apply border-none bg-transparent text-sm placeholder-black outline-none focus:placeholder-gray-400;
    }

    .settings-tag {
        @apply ml-auto rounded-sm bg-pink-500 px-2 py-0.5 text-center text-2xs font-normal text-white;
    }

    .settings-tag--yellow {
        @apply bg-yellow-300 font-bold tracking-tighter text-black;
    }

    .settings-tag--rainbow {
        @apply bg-gradient-to-r from-orange-500 via-purple-500 to-cyan-500 bg-clip-text text-xs font-black text-transparent;
    }

    .settings-tag--icon {
        @apply bg-transparent text-gray-500 transition-all hover:rotate-12 hover:scale-125 hover:text-pink-500;
    }

    .plan-features-footer {
        @apply sticky bottom-0 z-30 mt-auto flex w-full items-center justify-center bg-gradient-to-b from-gray-100/40 to-gray-100/80 px-2 pb-3 pt-2;
    }

    .plan-features-button {
        @apply whitespace-nowrap rounded-full bg-yellow-300 px-3 py-2 text-sm font-medium tracking-tight transition-all hover:scale-105 hover:bg-yellow-500;
    }

    .plan-features-button-icon {
        @apply ml-1 flex h-3.5 w-3.5 items-center text-xs;
    }

    :global(.cluster-highlight) {
        @apply bg-yellow-100 sepia-[100] filter transition-all duration-500;
    }

    :global(.cluster-highlight.remove) {
        @apply bg-transparent filter-none;
    }

    .sidebar-cluster-group {
        @apply flex min-h-6 cursor-pointer select-none flex-row items-center rounded py-0.5 pl-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200;
    }

    .sidebar-cluster-group--first {
        @apply mt-2;
    }

    .sidebar-list-wrapper {
        @apply pb-2;
    }

    .sidebar-list-title__button {
        @apply ml-auto mr-1 grid h-5 w-6 cursor-pointer place-items-center rounded opacity-100 hover:bg-gray-300 group-hover:opacity-100 sm:w-5 sm:opacity-0;
    }

    /* purgecss start ignore */
    :global(.sortable-clusters-wrapper .sortable-ghost) {
        @apply relative z-10 transform-none !important;
    }

    :global(.sortable-clusters-wrapper .sortable-ghost)::before {
        @apply absolute left-0 right-0 z-max h-0.5 w-full bg-gray-700 content-[''];
    }

    /* purgecss end ignore */
</style>
